import { createClient } from '@supabase/supabase-js';

// Get Supabase credentials from environment variables
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Validate that environment variables are set
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Please check your .env file contains:\n' +
    'REACT_APP_SUPABASE_URL=your_supabase_url\n' +
    'REACT_APP_SUPABASE_ANON_KEY=your_supabase_anon_key'
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    // Fix for multi-tab issues - prevent WebSocket conflicts
    multiTab: false, // Disable multi-tab support to prevent conflicts
    debug: process.env.NODE_ENV === 'development'
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    },
    // Disable realtime in multiple tabs to prevent conflicts
    heartbeatIntervalMs: 30000
  },
  // Add global configuration to prevent connection conflicts
  global: {
    headers: {
      'X-Client-Info': 'supabase-js-web'
    }
  }
});

// Debug: Log which Supabase project we're connecting to
console.log('🔗 Connecting to Supabase project:', supabaseUrl);
console.log('🔑 Using anon key:', supabaseAnonKey ? 'Present' : 'Missing');

// Add connection recovery logic
export const checkConnection = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.warn('⚠️ Supabase connection issue:', error);
      return false;
    }
    return true;
  } catch (err) {
    console.warn('⚠️ Supabase connection check failed:', err);
    return false;
  }
};

// Listen for visibility changes to reconnect when tab becomes active
// Use non-blocking approach to prevent deadlocks
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) {
    console.log('🔄 Tab became active, checking Supabase connection...');
    // Use setTimeout to make this non-blocking (prevents deadlocks)
    setTimeout(() => {
      checkConnection().then(isConnected => {
        if (!isConnected) {
          console.log('🔄 Attempting to reconnect to Supabase...');
          // Use getSession() instead of refreshSession() to avoid race conditions
          supabase.auth.getSession().then(({ data, error }) => {
            if (error) {
              console.warn('⚠️ Session refresh failed:', error);
            } else {
              console.log('✅ Supabase connection restored');
            }
          });
        } else {
          console.log('✅ Supabase connection is healthy');
        }
      });
    }, 0);
  }
});

// Add a periodic connection check every 30 seconds
// Use non-blocking approach to prevent deadlocks
let periodicCheckActive = false;
setInterval(() => {
  if (!document.hidden && !periodicCheckActive) {
    periodicCheckActive = true;
    // Use setTimeout to make this non-blocking
    setTimeout(() => {
      checkConnection().then(isConnected => {
        periodicCheckActive = false; // Reset flag
        if (!isConnected) {
          console.warn('⚠️ Periodic check: Supabase connection lost, attempting to reconnect...');
          // Use getSession() instead of refreshSession() to avoid race conditions
          supabase.auth.getSession().then(({ data, error }) => {
            if (error) {
              console.warn('⚠️ Periodic session refresh failed:', error);
            } else {
              console.log('✅ Periodic check: Supabase connection restored');
            }
          });
        }
      }).catch(() => {
        periodicCheckActive = false; // Reset flag on error
      });
    }, 0);
  }
}, 30000); // Check every 30 seconds 